import { useCallback } from 'react'

import { UseQueryResult, useQueries } from '@tanstack/react-query'

import { STATUS_NAMES } from '@/constants/task'

import { compareTaskDueDates } from '@/helpers/task'

import { pursuitActiveAssignedQueryKeys } from '@/hooks/pursuits/types'
import { fetchActiveAssignedPursuits } from '@/hooks/pursuits/useActiveAssignedPursuits'
import { tasksQueryKeys } from '@/hooks/tasks/types'
import { fetchUserTasks } from '@/hooks/tasks/useUserTasks'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { Pursuit } from '@/types/pursuit/Pursuit'
import type { Status as TaskStatus } from '@/types/task/Task'
import type { Task } from '@/types/task/Task'

const DEFAULT_TASK_STATUSES: TaskStatus[] = ['todo', 'in_progress', 'in_review']

const STATUS_ID_TO_NAME = STATUS_NAMES.reduce(
  (acc, status) => {
    acc[status.id] = status.title
    return acc
  },
  {} as Record<string, string>,
)

// some pursuits don't have due dates and marked with due dates after 2049, don't show them
const CUTOFF_YEAR = 2049

export type ActionItemType = 'pursuit' | 'task'

interface UseActionItemsProps {
  orgId: UUID | ''
}

interface ActionItem {
  type: ActionItemType
  id: UUID
  title: string
  due_date: Date | null
  status: string
  link: string | null
}

interface TaskActionItem extends ActionItem {
  type: 'task'
}

interface PursuitActionItem extends ActionItem {
  type: 'pursuit'
}

interface CombinedActionItemsResponse {
  data: PaginatedResponse<ActionItem>
  isLoading: boolean
  isPending: boolean
  isFetching: boolean
  isFetched: boolean
  allFetched: boolean
}

const combine = (
  results: UseQueryResult<PaginatedResponse<ActionItem>, Error>[],
): CombinedActionItemsResponse => {
  let data = results.reduce((acc: Array<ActionItem>, result) => {
    return [...acc, ...(result.data?.data ?? [])]
  }, [])
  if (data) {
    const cleanedData = data
      .sort((a, b) => compareTaskDueDates(a.due_date, b.due_date))
      .filter(
        (item) =>
          item.due_date === null || item.due_date.getFullYear() < CUTOFF_YEAR,
      )
    const count =
      results.reduce((acc: number, result) => {
        return acc + (result.data?.count ?? 0)
      }, 0) -
      (data.length - cleanedData.length)
    const has_more = results.some((result) => result.data?.has_more)

    const isLoading = results.some((result) => result.isLoading)
    const isPending = results.some((result) => result.isPending)
    const isFetching = results.some((result) => result.isFetching)
    const isFetched = results.every((result) => result.isFetched)
    const allFetched = results.every((result) => result.isFetched)

    return {
      data: {
        data: cleanedData,
        count: count,
        has_more: has_more,
      },
      isLoading,
      isPending,
      isFetching,
      isFetched,
      allFetched,
    }
  }
  return {
    data: {
      data: [],
      count: 0,
      has_more: false,
    },
    isLoading: false,
    isPending: false,
    isFetching: false,
    isFetched: true,
    allFetched: true,
  }
}

const selectPursuits = (
  data: PaginatedResponse<Pursuit>,
): PaginatedResponse<PursuitActionItem> => {
  return {
    data: data.data.map((pursuit) => ({
      type: 'pursuit',
      id: pursuit.id,
      title: pursuit.title,
      due_date: pursuit.due_date,
      status: pursuit.stage.name,
      link: `/orgs/${pursuit.organization_id}/pursuits/${pursuit.id}`,
    })),
    count: data.count,
    has_more: data.has_more,
  }
}

const selectTasks = (
  data: PaginatedResponse<Task>,
  orgId: UUID | '',
): PaginatedResponse<TaskActionItem> => {
  return {
    data: data.data.map((task) => ({
      type: 'task',
      id: task.id,
      title: task.title,
      due_date: task.due_date,
      status: STATUS_ID_TO_NAME[task.status] || task.status,
      link: task.pursuit_task
        ? `/orgs/${orgId}/pursuits/${task.pursuit_task.pursuit_id}/tasks/${task.id}`
        : null,
    })),
    count: data.count,
    has_more: data.has_more,
  }
}

export const useActionItems = ({ orgId }: UseActionItemsProps) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectTasksWithOrgId = useCallback(
    (data: PaginatedResponse<Task>) => {
      return selectTasks(data, orgId)
    },
    [orgId],
  )

  const queries = useQueries({
    queries: [
      {
        queryKey: pursuitActiveAssignedQueryKeys.organization({ orgId }),
        queryFn: () =>
          fetchActiveAssignedPursuits(orgId, accessToken, { limit: 10 }),
        staleTime: 1000 * 60 * 5, // 5 minutes
        select: selectPursuits,
      },
      {
        queryKey: tasksQueryKeys.user({
          orgId,
          status: DEFAULT_TASK_STATUSES,
          limit: 10,
          offset: 0,
          order_by: 'due_date',
          sort: 'asc',
        }),
        queryFn: () =>
          fetchUserTasks({
            orgId,
            filter: {
              status: DEFAULT_TASK_STATUSES,
            },
            pagination: {
              limit: 10,
              offset: 0,
            },
            sort: {
              order_by: 'due_date',
              sort: 'asc',
            },
            accessToken,
          }),
        staleTime: 1000 * 60 * 5, // 5 minutes
        select: selectTasksWithOrgId,
      },
    ],
    combine,
  })

  return queries
}
